import { useMutation, useQuery, useQueryClient } from 'react-query'

import { AccountingAccessCredentials } from 'src/helpers/accounting-access/accounting-access'
import {
  disconnectQuickbooks,
  getQuickBooksSyncConfig,
  getQuickbooksStatus,
  updateQuickBooksSyncConfig,
} from './quickbooks.api'
import { UpdateQuickBooksSyncingConfig } from './quickbooks.type'

// Poll every 15 seconds
const STATUS_POLL_INTERVAL_MS = 1000 * 15

export function useQuickBooksStatus({
  merchantId,
  credentials,
}: {
  merchantId: string | null
  credentials: AccountingAccessCredentials | undefined
}) {
  return useQuery({
    refetchInterval: STATUS_POLL_INTERVAL_MS,
    queryKey: ['quickbooks-status', merchantId, credentials],
    queryFn: () =>
      merchantId && credentials
        ? getQuickbooksStatus({ merchantId, credentials })
        : Promise.resolve(undefined),
  })
}

export function useQuickBooksSyncConfig({
  merchantId,
  credentials,
}: {
  merchantId: string
  credentials: AccountingAccessCredentials
}) {
  return useQuery({
    queryKey: ['quickbooks-sync-config', merchantId, credentials],
    queryFn: () => getQuickBooksSyncConfig({ merchantId, credentials }),
  })
}

export function useDisconnectQuickBooksMutation({
  merchantId,
  credentials,
}: {
  merchantId: string
  credentials: AccountingAccessCredentials
}) {
  const queryClient = useQueryClient()

  return useMutation(
    () => {
      return disconnectQuickbooks({
        merchantId,
        credentials,
      })
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['quickbooks-status'],
        })
      },
    },
  )
}

export function useRefreshQuickBooksAccountsListMutation({
  merchantId,
  credentials,
}: {
  merchantId: string
  credentials: AccountingAccessCredentials
}) {
  const queryClient = useQueryClient()

  return useMutation(
    () => {
      return getQuickBooksSyncConfig({
        merchantId,
        credentials,
        refreshAccountsList: true,
      })
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['quickbooks-sync-config'],
        })
      },
    },
  )
}

export function useUpdateQuickBooksSyncConfigMutation({
  merchantId,
  credentials,
}: {
  merchantId: string
  credentials: AccountingAccessCredentials
}) {
  const queryClient = useQueryClient()

  return useMutation(
    (syncConfig: UpdateQuickBooksSyncingConfig) =>
      updateQuickBooksSyncConfig({ merchantId, credentials, syncConfig }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['quickbooks-sync-config'],
        })

        await queryClient.invalidateQueries({
          queryKey: ['quickbooks-status'],
        })
      },
    },
  )
}
